import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Drawer,
  Empty,
  Grid,
  Input,
  Pagination,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import {
  AppstoreOutlined,
  BellOutlined,
  CheckOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  ReloadOutlined,
  SearchOutlined,
  ShopOutlined,
  SortAscendingOutlined,
  StarOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import {
  bulkDeleteNotifications,
  bulkMarkNotificationsAsRead,
  deleteNotificationById,
  fetchNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from '../store/slices/notificationsSlice';
import type { Notification } from '../types';
import PageShell from '../components/Layout/PageShell';

dayjs.extend(relativeTime);

const { Text } = Typography;

type SortKey = 'newest' | 'oldest' | 'unread_first';
type TypeFilter = '' | 'lead' | 'review' | 'listing' | 'store' | 'system';

function iconForType(type: Notification['type']) {
  const className = 'text-base';
  switch (type) {
    case 'lead':
      return <UserOutlined className={className} />;
    case 'review':
      return <StarOutlined className={className} />;
    case 'listing':
      return <AppstoreOutlined className={className} />;
    case 'store':
      return <ShopOutlined className={className} />;
    default:
      return <BellOutlined className={className} />;
  }
}

function iconWrapClass(type: Notification['type']) {
  switch (type) {
    case 'lead':
      return 'bg-cyan-50 text-cyan-700';
    case 'review':
      return 'bg-amber-50 text-amber-700';
    case 'listing':
      return 'bg-violet-50 text-violet-700';
    case 'store':
      return 'bg-sky-50 text-sky-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

function typeTagColor(type: Notification['type']) {
  switch (type) {
    case 'lead':
      return 'cyan';
    case 'review':
      return 'gold';
    case 'listing':
      return 'purple';
    case 'store':
      return 'blue';
    default:
      return 'default';
  }
}

function typeLabel(type: Notification['type']) {
  return type.charAt(0).toUpperCase() + type.slice(1);
}

const Notifications: React.FC = () => {
  const dispatch = useAppDispatch();
  const { notifications, unreadCount, pagination, loading, error } = useAppSelector((state) => state.notifications);
  const screens = Grid.useBreakpoint();
  const useCompactFilters = screens.md === false;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('newest');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchText.trim()), 350);
    return () => window.clearTimeout(t);
  }, [searchText]);

  const loadNotifications = useCallback(() => {
    dispatch(
      fetchNotifications({
        page,
        limit: pageSize,
        unread: unreadOnly || undefined,
        type: typeFilter || undefined,
        sort: sortKey,
        search: debouncedSearch || undefined,
      })
    );
  }, [dispatch, page, pageSize, unreadOnly, typeFilter, sortKey, debouncedSearch]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    setSelectedIds([]);
  }, [notifications]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allOnPageSelected = notifications.length > 0 && notifications.every((n) => selectedSet.has(n.id));

  const toggleSelectAllOnPage = (checked: boolean) => {
    if (checked) {
      setSelectedIds(notifications.map((n) => n.id));
      return;
    }
    setSelectedIds([]);
  };

  const onToggleOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      if (checked) return [...new Set([...prev, id])];
      return prev.filter((x) => x !== id);
    });
  };

  const onMarkSelectedRead = async () => {
    if (!selectedIds.length) return;
    await dispatch(bulkMarkNotificationsAsRead(selectedIds));
    loadNotifications();
  };

  const onDeleteSelected = async () => {
    if (!selectedIds.length) return;
    await dispatch(bulkDeleteNotifications(selectedIds));
    setSelectedIds([]);
    loadNotifications();
  };

  const onMarkAllRead = async () => {
    await dispatch(markAllNotificationsAsRead());
    loadNotifications();
  };

  const onMarkOneRead = async (id: string) => {
    await dispatch(markNotificationAsRead(id));
    loadNotifications();
  };

  const onDeleteOne = async (id: string) => {
    await dispatch(deleteNotificationById(id));
    loadNotifications();
  };

  const filterToolbar = (
    <Space direction="vertical" size="middle" className="w-full">
      <Input
        allowClear
        prefix={<SearchOutlined className="text-slate-400" />}
        placeholder="Search title or message"
        value={searchText}
        onChange={(e) => {
          setSearchText(e.target.value);
          setPage(1);
        }}
      />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Select<TypeFilter>
          value={typeFilter}
          onChange={(v) => {
            setTypeFilter(v);
            setPage(1);
          }}
          options={[
            { value: '', label: 'All types' },
            { value: 'lead', label: 'Lead' },
            { value: 'review', label: 'Review' },
            { value: 'listing', label: 'Listing' },
            { value: 'store', label: 'Store' },
            { value: 'system', label: 'System' },
          ]}
        />
        <Select<SortKey>
          value={sortKey}
          suffixIcon={<SortAscendingOutlined />}
          onChange={(v) => {
            setSortKey(v);
            setPage(1);
          }}
          options={[
            { value: 'newest', label: 'Newest first' },
            { value: 'oldest', label: 'Oldest first' },
            { value: 'unread_first', label: 'Unread first' },
          ]}
        />
        <div className="flex min-h-10 items-center rounded-lg border border-slate-200 bg-white px-3">
          <Checkbox
            checked={unreadOnly}
            onChange={(e) => {
              setUnreadOnly(e.target.checked);
              setPage(1);
            }}
            className="notification-unread-checkbox"
          >
            <span className="text-sm font-medium text-slate-700">Unread only</span>
          </Checkbox>
        </div>
      </div>
    </Space>
  );

  return (
    <PageShell
      title="Notifications"
      description="Track leads, reviews, and updates in one place. Filter, sort, and run bulk actions quickly."
      className="dashboard-surface"
      titleEnd={
        <Space wrap>
          <Badge count={unreadCount} showZero className="mr-1" />
          <Button icon={<ReloadOutlined />} onClick={loadNotifications} loading={loading}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            className="bg-[#0171BF]"
            onClick={onMarkAllRead}
            disabled={unreadCount === 0}
          >
            Mark all read
          </Button>
        </Space>
      }
    >
      {error ? <Alert type="error" showIcon message={error} className="mb-4 rounded-2xl" /> : null}

      <div className="space-y-5">
        <Card
          className="rounded-2xl border-slate-200 shadow-sm"
          title={<span className="font-semibold text-slate-900">Inbox</span>}
          extra={
            useCompactFilters ? (
              <Button icon={<FilterOutlined />} onClick={() => setFilterDrawerOpen(true)}>
                Filters
              </Button>
            ) : null
          }
        >
          {!useCompactFilters ? filterToolbar : null}

          <div className={useCompactFilters ? '' : 'mt-4'}>
            <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/70 p-2.5">
              <Checkbox checked={allOnPageSelected} onChange={(e) => toggleSelectAllOnPage(e.target.checked)}>
                Select page
              </Checkbox>
              <Button
                icon={<CheckOutlined />}
                onClick={onMarkSelectedRead}
                disabled={selectedIds.length === 0}
                size="small"
              >
                Mark selected read
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={onDeleteSelected}
                disabled={selectedIds.length === 0}
                size="small"
              >
                Delete selected
              </Button>
              <Text type="secondary" className="ml-auto text-xs">
                {selectedIds.length > 0 ? `${selectedIds.length} selected` : 'No selection'}
              </Text>
            </div>

            {loading && notifications.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 px-4 py-12 text-center">
                <Text type="secondary">Loading notifications...</Text>
              </div>
            ) : notifications.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No notifications match the current filters"
                className="py-8"
              />
            ) : (
              <div className="divide-y divide-slate-100">
                {notifications.map((n) => (
                  <div
                    key={n.id}
                    className={[
                      'flex gap-3 px-2 py-3 sm:px-3',
                      !n.read ? 'rounded-xl bg-[#0171BF]/[0.06]' : '',
                    ].join(' ')}
                  >
                    <Checkbox
                      className="mt-1"
                      checked={selectedSet.has(n.id)}
                      onChange={(e) => onToggleOne(n.id, e.target.checked)}
                    />
                    <div
                      className={['mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', iconWrapClass(n.type)].join(
                        ' '
                      )}
                    >
                      {iconForType(n.type)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={n.read ? 'font-medium text-slate-800' : 'font-semibold text-slate-900'}>{n.title}</span>
                        <Tag color={typeTagColor(n.type)} className="m-0">
                          {typeLabel(n.type)}
                        </Tag>
                        {!n.read ? (
                          <Tag color="processing" className="m-0">
                            Unread
                          </Tag>
                        ) : null}
                      </div>
                      <p className="mb-0 mt-1 text-sm leading-relaxed text-slate-600">{n.message}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        <span title={dayjs(n.createdAt).format('MMM D, YYYY h:mm A')}>{dayjs(n.createdAt).fromNow()}</span>
                        {n.readAt ? <span>Read {dayjs(n.readAt).fromNow()}</span> : null}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col gap-1 sm:flex-row sm:items-start">
                      {!n.read ? (
                        <Button type="text" size="small" icon={<CheckOutlined />} onClick={() => onMarkOneRead(n.id)} />
                      ) : null}
                      <Button danger type="text" size="small" icon={<DeleteOutlined />} onClick={() => onDeleteOne(n.id)} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {pagination && pagination.totalPages > 1 ? (
            <div className="mt-5 flex justify-center border-t border-slate-100 pt-4">
              <Pagination
                current={page}
                pageSize={pageSize}
                total={pagination.totalItems}
                showSizeChanger
                pageSizeOptions={['10', '20', '50']}
                onChange={(p, ps) => {
                  setPage(p);
                  setPageSize(ps);
                }}
              />
            </div>
          ) : null}
        </Card>

        <Card className="rounded-2xl border-slate-200 shadow-sm !mt-3">
          <div className="flex items-start text-slate-600">
            <ExclamationCircleOutlined className="mt-1 mr-3 text-[#0171BF]" />
            <Text type="secondary">
              Notifications are synchronized from your lead and review activity. Read and delete actions are persisted to your account.
            </Text>
          </div>
        </Card>
      </div>

      <Drawer
        title="Filters"
        placement="bottom"
        height="min(85vh, 480px)"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
      >
        {filterToolbar}
        <Button type="primary" block className="mt-5 bg-[#0171BF]" onClick={() => setFilterDrawerOpen(false)}>
          Apply
        </Button>
      </Drawer>
    </PageShell>
  );
};

export default Notifications;
